import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { heroGradient, marketingKeyframes, glowOrb, accentColors } from "./marketingStyles";
import {
  marketingHeroImage,
  marketingHeroPhotos,
} from "./marketingHeroConfig";
import {
  heroCarouselFadeMs,
  heroCarouselIntervalMs,
} from "./marketingPhotosConfig";

const heroImageOverlay = `linear-gradient(
  135deg,
  rgba(10, 10, 10, 0.94) 0%,
  rgba(10, 10, 10, 0.86) 40%,
  rgba(10, 10, 10, 0.78) 100%
)`;

/**
 * Full-width marketing hero shell with optional rotating background photos.
 * Falls back to animated CSS gradient when no images are configured.
 */
const MarketingHeroBackground = ({
  children,
  photos = marketingHeroPhotos,
  imageSrc = marketingHeroImage.src,
  imageAlt = marketingHeroImage.alt,
  sx,
  ...rest
}) => {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const activePhotos = photos.length > 0 ? photos : imageSrc ? [{ src: imageSrc, alt: imageAlt }] : [];
  const hasImages = activePhotos.length > 0;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion || activePhotos.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % activePhotos.length);
    }, heroCarouselIntervalMs);

    return () => clearInterval(interval);
  }, [prefersReducedMotion, activePhotos.length]);

  return (
    <Box
      sx={{
        ...marketingKeyframes,
        position: "relative",
        overflow: "hidden",
        borderBottom: 1,
        borderColor: "divider",
        ...(hasImages
          ? { bgcolor: "background.default" }
          : {
              background: heroGradient,
              backgroundSize: "200% 200%",
              animation: "gradientShift 12s ease infinite",
            }),
        ...sx,
      }}
      {...rest}
    >
      {hasImages && (
        <>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          >
            {activePhotos.map((photo, index) => {
              const isActive = index === activeIndex;
              return (
                <Box
                  key={photo.src}
                  component="img"
                  src={photo.src}
                  alt={isActive ? photo.alt : ""}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: photo.objectPosition ?? "center 40%",
                    opacity: isActive ? 1 : 0,
                    transition: prefersReducedMotion
                      ? "none"
                      : `opacity ${heroCarouselFadeMs}ms ease-in-out`,
                  }}
                />
              );
            })}
          </Box>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: heroImageOverlay,
              pointerEvents: "none",
            }}
          />
        </>
      )}

      <Box sx={{ ...glowOrb(accentColors[1], 360), top: -80, right: -60 }} />
      <Box sx={{ ...glowOrb(accentColors[0], 240), bottom: -40, left: -40 }} />

      <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
    </Box>
  );
};

export default MarketingHeroBackground;
